// @ts-check

/**
 * @file Pure-JS HMAC-SHA256 fallback for offline-grant verification.
 *
 * Framework verification of offline grants requires the WebCrypto subtle API.
 * Browsers expose crypto.subtle only in secure contexts, so a build served
 * over plain http (the Android Chrome system tests reach the app over the
 * unencrypted emulator host address) cannot verify its own grants and every
 * replica open fails with sync-failed. This module reproduces the framework's
 * exact signature contract — HS256 over the stable-JSON envelope with the
 * hmac-sha256 prefix — without any crypto provider, so verification works in
 * every context. It is verification-only: signing new grants stays a backend
 * concern.
 */

/** @import {OfflineGrant, SignedOfflineGrant} from "velocious/build/src/sync/offline-grant.js" */
/** @import {FrontendModelSyncJsonValue} from "velocious/build/src/configuration-types.js" */

const SIGNATURE_ALGORITHM = "HS256"
const SIGNATURE_PREFIX = "hmac-sha256-"
const SHA256_ROUND_CONSTANTS = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
]

/**
 * Encodes a string as UTF-8 bytes.
 * @param {string} value Text to encode.
 * @returns {number[]} Byte array.
 */
function utf8Bytes(value) {
  return Array.from(new TextEncoder().encode(value))
}

/**
 * Computes SHA-256 over a byte array.
 * @param {number[]} bytes Input bytes.
 * @returns {number[]} Eight 32-bit words, big-endian.
 */
function sha256Words(bytes) {
  const bitLength = bytes.length * 8
  const padded = [...bytes, 0x80]
  while (padded.length % 64 !== 56) padded.push(0)
  // Append the 64-bit big-endian message length: high 32 bits then low 32 bits.
  const highWord = Math.floor(bitLength / 2 ** 32)
  const lowWord = bitLength >>> 0
  for (let shift = 24; shift >= 0; shift -= 8) padded.push((highWord >>> shift) & 0xff)
  for (let shift = 24; shift >= 0; shift -= 8) padded.push((lowWord >>> shift) & 0xff)

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19
  /** @type {number[]} */
  const schedule = new Array(64)

  for (let chunk = 0; chunk < padded.length; chunk += 64) {
    for (let index = 0; index < 16; index += 1) {
      schedule[index] = ((padded[chunk + index * 4] << 24) | (padded[chunk + index * 4 + 1] << 16) | (padded[chunk + index * 4 + 2] << 8) | padded[chunk + index * 4 + 3]) >>> 0
    }
    for (let index = 16; index < 64; index += 1) {
      const s0 = rotateRight(schedule[index - 15], 7) ^ rotateRight(schedule[index - 15], 18) ^ (schedule[index - 15] >>> 3)
      const s1 = rotateRight(schedule[index - 2], 17) ^ rotateRight(schedule[index - 2], 19) ^ (schedule[index - 2] >>> 10)
      schedule[index] = (schedule[index - 16] + s0 + schedule[index - 7] + s1) >>> 0
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7
    for (let index = 0; index < 64; index += 1) {
      const s1 = (rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)) >>> 0
      const ch = ((e & f) ^ (~e & g)) >>> 0
      const temp1 = (h + s1 + ch + SHA256_ROUND_CONSTANTS[index] + schedule[index]) >>> 0
      const s0 = (rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22)) >>> 0
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0
      const temp2 = (s0 + maj) >>> 0
      h = g; g = f; f = e; e = (d + temp1) >>> 0
      d = c; c = b; b = a; a = (temp1 + temp2) >>> 0
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
}

/**
 * Computes SHA-256 hex over a string.
 * @param {string} value Text to hash.
 * @returns {string} Lowercase hex digest.
 */
export function sha256Hex(value) {
  return sha256Words(utf8Bytes(value)).map((word) => word.toString(16).padStart(8, "0")).join("")
}

/**
 * 32-bit right rotation.
 * @param {number} value Word to rotate.
 * @param {number} count Bits.
 * @returns {number} Rotated word.
 */
function rotateRight(value, count) {
  return ((value >>> count) | (value << (32 - count))) >>> 0
}

/**
 * Computes HMAC-SHA256 and returns the framework's prefixed hex signature.
 * @param {{message: string, secret: string}} args Message and key.
 * @returns {string} `hmac-sha256-`-prefixed lowercase hex signature.
 */
export function hmacSha256Hex({message, secret}) {
  let keyBytes = utf8Bytes(secret)
  if (keyBytes.length > 64) keyBytes = flattenWords(sha256Words(keyBytes))

  const outerPad = new Array(64).fill(0x5c)
  const innerPad = new Array(64).fill(0x36)
  for (let index = 0; index < keyBytes.length; index += 1) {
    outerPad[index] = keyBytes[index] ^ 0x5c
    innerPad[index] = keyBytes[index] ^ 0x36
  }

  const innerDigest = sha256Words([...innerPad, ...utf8Bytes(message)])
  return `${SIGNATURE_PREFIX}${flattenWords(sha256Words([...outerPad, ...flattenWords(innerDigest)])).map((byte) => byte.toString(16).padStart(2, "0")).join("")}`
}

/**
 * Expands eight big-endian 32-bit words into 32 bytes.
 * @param {number[]} words Hash words.
 * @returns {number[]} Byte array.
 */
function flattenWords(words) {
  /** @type {number[]} */
  const bytes = []
  for (const word of words) {
    bytes.push((word >>> 24) & 0xff, (word >>> 16) & 0xff, (word >>> 8) & 0xff, word & 0xff)
  }
  return bytes
}

/**
 * Verifies a signed offline grant without any crypto provider, reproducing the
 * framework contract: envelope shape checks, key-id lookup, HMAC comparison
 * over the stable-JSON signature input, and expiry check.
 * @param {object} args Verification inputs.
 * @param {SignedOfflineGrant} args.signedGrant Envelope to verify.
 * @param {{current?: boolean, id: string, secret: string}[]} args.signingKeys Candidate verification keys.
 * @param {() => Date} [args.now] Trusted verification clock.
 * @returns {OfflineGrant} Verified grant payload.
 * @throws {Error} When the envelope, signature, key id, or TTL check fails.
 */
export function verifyOfflineGrantWithoutWebCrypto({now = () => new Date(), signedGrant, signingKeys}) {
  if (!signedGrant || typeof signedGrant !== "object" || Array.isArray(signedGrant)) throw new Error("Expected signed offline grant object")
  if (signedGrant.algorithm !== SIGNATURE_ALGORITHM) throw new Error(`Unsupported offline grant signature algorithm '${String(signedGrant.algorithm)}'`)
  if (typeof signedGrant.keyId !== "string" || signedGrant.keyId.length < 1) throw new Error("Expected offline grant key id")
  if (typeof signedGrant.signature !== "string" || !signedGrant.signature.match(/^hmac-sha256-[a-f0-9]{64}$/u)) throw new Error("Expected offline grant signature")

  const grant = normalizeGrant(signedGrant.grant)
  const signingKey = signingKeys.find((key) => key.id === signedGrant.keyId)
  if (!signingKey) throw new Error(`No offline grant signing key for key id '${signedGrant.keyId}'`)

  const expectedSignature = hmacSha256Hex({
    message: stableJsonStringify({algorithm: signedGrant.algorithm, grant, keyId: signedGrant.keyId}),
    secret: signingKey.secret
  })
  if (expectedSignature !== signedGrant.signature) throw new Error("Offline grant signature did not match")

  const verificationTime = now()
  if (Number.isNaN(verificationTime.getTime())) throw new Error("Invalid offline grant verification time")
  if (new Date(grant.expiresAt).getTime() <= verificationTime.getTime()) throw new Error("Offline grant expired")

  return grant
}

/**
 * Normalizes a grant payload exactly like the framework does.
 * @param {unknown} grant Raw grant payload.
 * @returns {OfflineGrant} Normalized grant.
 * @throws {Error} When a required field is missing or malformed.
 */
function normalizeGrant(grant) {
  if (!grant || typeof grant !== "object" || Array.isArray(grant)) throw new Error("Expected offline grant object")
  const record = /** @type {Record<string, unknown>} */ (grant)
  return {
    deviceId: requiredString(record.deviceId, "deviceId"),
    expiresAt: isoDateString(new Date(requiredString(record.expiresAt, "expiresAt")), "expiresAt"),
    grantId: requiredString(record.grantId, "grantId"),
    issuedAt: isoDateString(new Date(requiredString(record.issuedAt, "issuedAt")), "issuedAt"),
    resources: deterministicJsonObject({label: "resources", value: record.resources}),
    scopes: deterministicJsonObject({label: "scopes", value: record.scopes}),
    userId: requiredString(record.userId, "userId")
  }
}

/**
 * Requires a non-empty string field.
 * @param {unknown} value Field value.
 * @param {string} name Field name.
 * @returns {string} Validated string.
 * @throws {Error} When the field is not a non-empty string.
 */
function requiredString(value, name) {
  if (typeof value !== "string" || value.length < 1) throw new Error(`Expected offline grant ${name}`)
  return value
}

/**
 * Normalizes a Date to an ISO timestamp.
 * @param {Date} date Date value.
 * @param {string} label Field label.
 * @returns {string} ISO timestamp.
 * @throws {Error} When the date is invalid.
 */
function isoDateString(date, label) {
  if (Number.isNaN(date.getTime())) throw new Error(`Invalid offline grant ${label}`)
  return date.toISOString()
}

/**
 * Stable JSON stringifier with sorted object keys (framework contract).
 * @param {unknown} value Value to serialize.
 * @returns {string} Deterministic JSON string.
 */
function stableJsonStringify(value) {
  return JSON.stringify(deterministicJson({label: "root", value}))
}

/**
 * Normalizes a value as a deterministic JSON object.
 * @param {{label: string, value: unknown}} args Input.
 * @returns {Record<string, FrontendModelSyncJsonValue>} Normalized object.
 * @throws {Error} When the value is not a plain object.
 */
function deterministicJsonObject({label, value}) {
  const normalized = deterministicJson({label, value})
  if (!normalized || typeof normalized !== "object" || Array.isArray(normalized)) throw new Error(`Expected offline grant ${label} object`)
  return /** @type {Record<string, FrontendModelSyncJsonValue>} */ (normalized)
}

/**
 * Normalizes deterministic JSON with sorted object keys.
 * @param {{label: string, value: unknown}} args Input.
 * @returns {unknown} Normalized JSON value.
 * @throws {Error} When the value is not deterministic JSON.
 */
function deterministicJson({label, value}) {
  if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value
  if (Array.isArray(value)) return value.map((entry, index) => deterministicJson({label: `${label}/${index}`, value: entry}))
  if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
    /** @type {Record<string, unknown>} */
    const normalized = {}
    for (const key of Object.keys(value).sort()) {
      const childValue = /** @type {Record<string, unknown>} */ (value)[key]
      if (childValue === undefined) continue
      normalized[key] = deterministicJson({label: `${label}/${key}`, value: childValue})
    }
    return normalized
  }
  throw new Error(`Offline grant ${label} must be deterministic JSON`)
}
